/**
 * Un intento de orden SPEI POR NODO de origen, con tope (feature 124).
 *
 * Es la regla de `SpeiOrderAttempt` aplicada nodo a nodo: misma huella ⇒ las
 * MISMAS llaves; otra huella ⇒ llaves nuevas (sustituye el intento de ESE nodo);
 * `settle(nodo)` ⇒ el siguiente intento de ese nodo es nuevo. Cambiar de nodo no
 * toca nada: el intento del nodo que se deja sigue ahí para cuando se vuelva.
 *
 * Por qué existe: con un solo intento por página, A (desenlace desconocido) → B →
 * A estrenaba llave, y banca (#281) no reconocía el reenvío del MISMO cuerpo como
 * el mismo intento ⇒ posible doble SPEI.
 *
 * - El `Map` conserva el orden de inserción: "tocar" un nodo (borrar y volver a
 *   insertar) lo deja al final, así que la PRIMERA clave es la usada hace más
 *   tiempo y el desalojo (LRU) es O(1).
 * - `create` llega en cada `keyFor` (no en el constructor) porque el llamador
 *   puede guardar en K datos del cuerpo que abrió el intento (la referencia de la
 *   individual).
 * - Vive SOLO en memoria: nada de esto se persiste ni se registra.
 *
 * TypeScript puro; no sustituye a `SpeiOrderAttempt` (SPEI no cambia).
 */
export class SpeiAttemptsByNode<K> {
  private readonly attempts = new Map<string, { readonly fingerprint: string; readonly key: K }>();

  constructor(private readonly capacity: number) {}

  /** Las llaves del intento de `node` para esta huella (las mismas si coincide; nuevas si no). */
  keyFor(node: string, fingerprint: string, create: () => K): K {
    const current = this.attempts.get(node);
    this.attempts.delete(node);
    if (current && current.fingerprint === fingerprint) {
      this.attempts.set(node, current);
      return current.key;
    }
    const fresh = { fingerprint, key: create() };
    this.attempts.set(node, fresh);
    this.evict();
    return fresh.key;
  }

  /** Registro confirmado o "reintentar como nuevo": el siguiente intento de ESE nodo es nuevo. */
  settle(node: string): void {
    this.attempts.delete(node);
  }

  /** El intento sin resolver de `node`, o `null`. No crea nada. */
  peek(node: string): K | null {
    const current = this.attempts.get(node);
    if (!current) return null;
    this.attempts.delete(node);
    this.attempts.set(node, current);
    return current.key;
  }

  /** Cierre de sesión: no sobrevive ningún intento. */
  clear(): void {
    this.attempts.clear();
  }

  get size(): number {
    return this.attempts.size;
  }

  private evict(): void {
    while (this.attempts.size > this.capacity) {
      const oldest = this.attempts.keys().next().value;
      if (oldest === undefined) return;
      this.attempts.delete(oldest);
    }
  }
}
